import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import GenericModal, { NON_DISMISSIBLE_MODAL_PROPS } from './GenericModal';
import QuickEditObjectiveModal from './QuickEditObjectiveModal';
import { Sparkles } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import objetivosApi from '../../api/objetivos';
import usersApi, { setupSteps } from '../../api/user';
import { formatCurrencyAmount } from '../../utils/currency';
import ObjectivesSetupView from '../ObjectivesSetupView';
import { useGoogleAds } from '../../hooks/useGoogleAds';
import { useMetaAds } from '../../hooks/useMetaAds';
import optimizationsApi from '../../api/optimizations';
import { useOptimizationGenerationToast } from '../context/OptimizationGenerationToastContext';
import { normalizeGroupsResponse } from '../../utils/optimizationTransformers';

const PLATFORM_GOOGLE = 'GOOGLE';
const PLATFORM_META = 'META';

const CreateObjectivesSetupModal = ({
  isOpen,
  onClose,
  platform,
  mandatoryUntilGenerated = false,
}) => {
  const { auth, updateContextUser } = useContext(AuthContext);
  const { runGeneration } = useOptimizationGenerationToast();
  const {
    isConnected: isGoogleConnected,
    activeAccount: activeGoogleAccount,
    loadingActiveAccount: loadingGoogleAccount,
  } = useGoogleAds();
  const {
    isConnected: isMetaConnected,
    activeAccount: activeMetaAccount,
    loadingActiveAccount: loadingMetaAccount,
  } = useMetaAds();

  const [selectedConversions, setSelectedConversions] = useState([]);
  const [selectedObjectiveMetrics, setSelectedObjectiveMetrics] = useState([]);
  const [objectivesGenerated, setObjectivesGenerated] = useState(false);
  const [objectives, setObjectives] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [quickEditObjectiveId, setQuickEditObjectiveId] = useState(null);

  const platformKey = platform === PLATFORM_META ? PLATFORM_META : PLATFORM_GOOGLE;

  const googleConversions = useMemo(() => {
    if (!isGoogleConnected || !activeGoogleAccount || platformKey !== PLATFORM_GOOGLE) return [];
    return (activeGoogleAccount.conversion_actions || [])
      ?.filter((action) => action && action.status === 'ENABLED')
      .map((action) => ({
        id: action?.id || '',
        nombre: action?.name || '',
        plataforma: PLATFORM_GOOGLE,
        monthlyConversions: Number(action?.monthly_conversions ?? action?.['metrics.conversions'] ?? 0) || 0,
      })) || [];
  }, [isGoogleConnected, activeGoogleAccount, platformKey]);

  const metaConversions = useMemo(() => {
    if (!isMetaConnected || !activeMetaAccount || platformKey !== PLATFORM_META) return [];
    return (activeMetaAccount.pixels?.data || [])?.flatMap((pixel) =>
      (pixel?.existing_events || [])?.map((event) => ({
        id: `${pixel?.id || ''}_${event || ''}`,
        nombre: `${pixel?.name || ''} - ${event || ''}`,
        plataforma: PLATFORM_META,
        monthlyConversions: Number(pixel?.monthly_event_counts?.[event] ?? 0) || 0,
      })) || []
    ) || [];
  }, [isMetaConnected, activeMetaAccount, platformKey]);

  const loadingConversions = platformKey === PLATFORM_GOOGLE ? loadingGoogleAccount : loadingMetaAccount;

  useEffect(() => {
    if (isOpen) {
      setSelectedConversions([]);
      setSelectedObjectiveMetrics([]);
      setObjectivesGenerated(false);
      setObjectives([]);
      setIsProcessing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setQuickEditObjectiveId(null);
  }, [isOpen]);

  const toggleConversion = useCallback((id) => {
    setSelectedConversions((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  }, []);

  const toggleObjectiveMetric = useCallback((metrica) => {
    setSelectedObjectiveMetrics((prev) =>
      prev.includes(metrica) ? prev.filter((m) => m !== metrica) : [...prev, metrica]
    );
  }, []);

  const selectAllForPlatform = useCallback(
    (plat) => {
      const ids = plat === PLATFORM_GOOGLE
        ? googleConversions.map((c) => c.id)
        : metaConversions.map((c) => c.id);
      setSelectedConversions((prev) => {
        const without = prev.filter((p) => !ids.includes(p));
        const allSelected = ids.length > 0 && ids.every((id) => prev.includes(id));
        return allSelected ? without : [...new Set([...without, ...ids])];
      });
    },
    [googleConversions, metaConversions]
  );

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const conversionIds = Array.isArray(selectedConversions) ? selectedConversions : [];
      const objectiveMetrics = Array.isArray(selectedObjectiveMetrics)
        ? selectedObjectiveMetrics
        : [];
      const response =
        platformKey === PLATFORM_GOOGLE
          ? await objetivosApi.autoGenerateGoogle(conversionIds, objectiveMetrics)
          : await objetivosApi.autoGenerateMeta(conversionIds, objectiveMetrics);
      const allObjectives = (response?.data?.objetivos || response?.data || []) || [];
      if (allObjectives.length === 0) {
        toast.error(
          'Tu cuenta todavía no tiene datos suficientes para poder crearte tus objetivos 😱\nApenas tengamos más información, los objetivos se crearán automáticamente 👌'
        );
        setIsGenerating(false);
        return;
      }
      const hasGoogleConnected = Boolean(auth?.user?.google_ads_customer_id);
      const hasMetaConnected = Boolean(auth?.user?.meta_ad_account_id);
      (async () => {
        try {
          let completedSteps = [setupSteps.CREA_OBJETIVOS_AUTOMATICOS_1];
          if (hasGoogleConnected && hasMetaConnected) {
            completedSteps.push(setupSteps.CREA_OBJETIVOS_AUTOMATICOS_2);
          }
          await usersApi.setSetupSteps(completedSteps);
          const currentCompleted = auth?.user?.setup_steps_completed || [];
          const updated = [...new Set([...currentCompleted, ...completedSteps])];
          updateContextUser({ setup_steps_completed: updated });
        } catch (err) {
          console.error('Error actualizando setup_step:', err);
        }
      })();
      setObjectives(allObjectives);
      setObjectivesGenerated(true);
      toast.success('¡Objetivos creados con éxito! 🎯');
    } catch (error) {
      console.error('Error generando objetivos:', error);
      toast.error(error.response?.data?.error || 'Error al generar objetivos automáticos');
    } finally {
      setIsGenerating(false);
    }
  }, [
    platformKey,
    selectedConversions,
    selectedObjectiveMetrics,
    auth?.user?.google_ads_customer_id,
    auth?.user?.meta_ad_account_id,
    auth?.user?.setup_steps_completed,
    updateContextUser,
  ]);

  const getMetricEmoji = (metrica) => {
    const m = (metrica || '').toUpperCase();
    if (m === 'ROAS') return '💰';
    if (m === 'CPA') return '⚖️';
    if (m === 'PRESUPUESTO' || m === 'INVERSIÓN') return '💸';
    if (m === 'CONVERSIONES') return '🛒';
    return '🎯';
  };
  const formatTarget = (target, metrica, currency = null) => {
    if (target !== 0 && !target) return 'N/A';
    try {
      const numTarget = parseFloat(target);
      if (isNaN(numTarget)) return 'N/A';
      switch (metrica) {
        case 'ROAS':
          return `${numTarget.toFixed(2)}x`;
        case 'CPA':
        case 'Presupuesto':
          return formatCurrencyAmount(numTarget, currency, {
            locale: 'es-ES',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        case 'Conversiones':
          return `${Math.floor(numTarget).toLocaleString('es-ES')}`;
        default:
          return target.toString();
      }
    } catch {
      return 'N/A';
    }
  };
  const formatFecha = (fechaStr) => {
    if (!fechaStr) return 'N/A';
    if (typeof fechaStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaStr)) {
      const [year, month, day] = fechaStr.split('-').map(Number);
      const fecha = new Date(year, month - 1, day);
      return fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return new Date(fechaStr).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleClose = useCallback(() => {
    if (objectivesGenerated && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('objectives-created'));
    }
    onClose();
  }, [objectivesGenerated, onClose]);

  const handleFinalizar = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const brandId = auth?.user?.active_brand_id;
      if (!brandId) {
        toast.error('No hay marca activa seleccionada para generar optimizaciones');
        return;
      }

      onClose();

      const platforms =
        platformKey === PLATFORM_META ? ['meta'] : ['google'];
      const result = await runGeneration({
        brandId,
        platforms,
        loadCount: async () => {
          const response = await optimizationsApi.getActiveOptimizations();
          const { groups } = normalizeGroupsResponse(response?.data);
          return groups.length;
        },
      });
      if (!result.ok) return;

      if (result.count === 0) {
        toast('No encontramos optimizaciones nuevas por ahora');
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('objectives-created'));
      }
    } catch (error) {
      console.error('Error ejecutando motor de optimizaciones:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'No se pudieron generar las optimizaciones';
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditarObjetivo = (objetivo) => {
    if (!objetivo?.id) return;
    setQuickEditObjectiveId(objetivo.id);
  };

  const handleQuickEditSaved = useCallback((updated) => {
    if (!updated?.id) return;
    setObjectives((prev) =>
      prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o))
    );
  }, []);

  const googleConv = platformKey === PLATFORM_GOOGLE ? googleConversions : [];
  const metaConv = platformKey === PLATFORM_META ? metaConversions : [];

  const dismissibleModalProps = mandatoryUntilGenerated
    ? NON_DISMISSIBLE_MODAL_PROPS
    : {
        closable: true,
        shouldCloseOnOverlayClick: true,
        shouldCloseOnEsc: true,
      };

  return (
    <>
    <QuickEditObjectiveModal
      isOpen={quickEditObjectiveId != null}
      objectiveId={quickEditObjectiveId}
      onClose={() => setQuickEditObjectiveId(null)}
      onSaved={handleQuickEditSaved}
      overlayZIndex={10100}
    />
    <GenericModal
      {...dismissibleModalProps}
      isOpen={isOpen}
      toggle={handleClose}
      titleSlot={
        !objectivesGenerated ? null : (
          <div className="flex flex-col items-center w-full">
            <h2 className="text-xl md:text-2xl font-bold text-center w-full text-white">
              ¡Listo! Objetivos creados con éxito.
            </h2>
          </div>
        )
      }
      description={
        objectivesGenerated
          ? 'El siguiente paso es buscar optimizaciones para tus anuncios. Podés editar los objetivos abajo o continuar con el siguiente paso.'
          : ''
      }
      onClose={handleClose}
      showSubmitButton={false}
      showLogo={false}
      width={objectivesGenerated ? '640px' : 'min(92vw, 880px)'}
      contentPadding="2.5rem 2rem"
    >
      <div className="space-y-6">
        {isGenerating ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full" />
              <div className="absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin" />
              <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-purple-400 animate-pulse" />
            </div>
            <p className="text-white/90 font-medium">Generando objetivos con IA...</p>
            <p className="text-white/60 text-sm">Analizando tu cuenta y creando objetivos personalizados.</p>
          </div>
        ) : !objectivesGenerated ? (
          <ObjectivesSetupView
            googleConversions={googleConv}
            metaConversions={metaConv}
            selectedConversions={selectedConversions}
            onToggleConversion={toggleConversion}
            onSelectAllForPlatform={selectAllForPlatform}
            selectedObjectiveMetrics={selectedObjectiveMetrics}
            onToggleObjectiveMetric={toggleObjectiveMetric}
            onGenerate={handleGenerate}
            loading={loadingConversions}
            isGoogleConnected={platformKey === PLATFORM_GOOGLE && isGoogleConnected}
            isMetaConnected={platformKey === PLATFORM_META && isMetaConnected}
            compact
          />
        ) : (
          <>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-2">
              {objectives.map((objetivo) => {
                const metrica = objetivo.metrica_relacionada || '';
                const emoji = getMetricEmoji(metrica);
                const targetStr = formatTarget(objetivo.target, metrica, objetivo.currency);
                const fechaInicio = formatFecha(objetivo.fecha_inicio);
                const fechaFin = formatFecha(objetivo.fecha_fin);
                return (
                  <div
                    key={objetivo.id}
                    className="backdrop-blur-md bg-white/5 border border-white/10 rounded-lg p-2.5 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <span className="text-lg shrink-0">{emoji}</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-semibold text-white leading-tight">
                            {metrica}: {targetStr} · {fechaInicio} – {fechaFin}
                          </p>
                          <p className="text-xs font-normal text-white/70 truncate leading-tight">{objetivo.nombre}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleEditarObjetivo(objetivo)}
                        className="shrink-0 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium transition-all duration-200"
                      >
                        Editar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-white/75 text-sm text-center">
              Al continuar vamos a buscar optimizaciones para tus anuncios. Se cierra este modal y te mostramos el avance.
            </p>
            <div className="pt-4">
              <button
                type="button"
                onClick={handleFinalizar}
                disabled={isProcessing}
                className="w-full min-h-[48px] px-6 py-4 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 text-white rounded-xl font-bold text-base hover:from-purple-500 hover:via-blue-500 hover:to-purple-500 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
              >
                {isProcessing ? 'Iniciando…' : 'Buscar optimizaciones para mis anuncios'}
              </button>
            </div>
          </>
        )}
      </div>
    </GenericModal>
    </>
  );
};

export default CreateObjectivesSetupModal;
